import { Badge, StarRating } from '@petsmart-ui/sparky';

import { decodeEntityString } from '@libs/algolia';
import { encodeFref, type FrefListCodes } from '@libs/common-next';
import { type BuyItAgainItem } from '@libs/common-types';

import { useSelectProductEvent, useTrackProduct } from '../../hooks';
import { Price } from './components';

import './carousel-product-card.scss';

export type CarouselProductCardProps = {
    recommendation: BuyItAgainItem;
    /**
     * Where this card actually shows up in the row, counting from 1.
     * Not the same as `recommendationRank`. Some products get filtered out before we render.
     */
    position: number;
    frefAttribution: FrefListCodes;
    onView?: () => void;
};

export const CarouselProductCard = ({
    recommendation,
    position,
    frefAttribution,
    onView,
}: CarouselProductCardProps) => {
    const sendSelectEvent = useSelectProductEvent(recommendation, { position, frefAttribution });

    const { name, url, image, price, rating, badges, unitsPurchasedLast24Months: purchasedTimes } = recommendation;

    const title = decodeEntityString(name);
    const badgeText = badges?.[0];
    const frefQuery = encodeFref({ ...frefAttribution, index: position });
    const href = `${url}${url.includes('?') ? '&' : '?'}${frefQuery}`;

    const { ref } = useTrackProduct({ product: recommendation, onView });

    return (
        <div className="carousel-product-card" data-testid="product-carousel-item" ref={ref}>
            <div className="carousel-product-card__header">
                <h3 className="carousel-product-card__title">
                    <a href={href} className="carousel-product-card__text-link" onClickCapture={sendSelectEvent}>
                        {title}
                    </a>
                </h3>
                <img className="carousel-product-card__image" src={image} alt={title} aria-hidden="true" />
                <span className="carousel-product-card__meta">
                    Purchased {purchasedTimes} {purchasedTimes === 1 ? 'time' : 'times'}
                </span>
            </div>

            <div className="carousel-product-card__footer">
                {badgeText && <Badge className="sparky-c-product-card__badge" text={badgeText} aria-hidden="true" />}
                {rating.average !== undefined && (
                    <div className="rating">
                        <span className="visually-hidden">{`${rating.average} out of 5 stars selected ${rating.count} reviews`}</span>
                        <StarRating
                            className="sparky-c-product-card__star-rating"
                            value={rating.average}
                            ratingAfter={`(${rating.count})`}
                            size="sm"
                            aria-hidden="true"
                        />
                    </div>
                )}
                {price && (
                    <div className="price">
                        <Price price={price} />
                    </div>
                )}
            </div>
        </div>
    );
};
